import { GraviGrid, Horizontal, Texto } from '@gravitate-js/excalibrr'
import { GridApi, GridOptions } from 'ag-grid-community'
import { Alert, Select } from 'antd'
import React, { useMemo } from 'react'

interface GridContainerProps {
  loading: boolean
  isError: boolean
  error: Error | null
  entityType: string
  totalRows: number
  gridOptions: GridOptions
  onUpdate: (updatedRow: Record<string, unknown>) => Promise<void>
  actionButtons: React.ReactNode[]
  customSearchBar?: React.ReactNode
  pageSize: number
  pageSizeOptions: number[]
  onPageSizeChange: (size: number) => void
  gridApiRef?: React.MutableRefObject<GridApi | null>
}

/**
 * Grid container component for MasterDataGrid
 */
export const GridContainer: React.FC<GridContainerProps> = ({
  loading,
  isError,
  entityType,
  totalRows,
  gridOptions,
  onUpdate,
  actionButtons,
  customSearchBar,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
  gridApiRef,
}) => {
  // If there's an error and no grid config, show error state
  if (isError) {
    return (
      <div className='h-full flex items-center justify-center'>
        <Alert
          message='Error Loading Data'
          description={`Unable to load ${entityType} configuration. Please try again later.`}
          type='error'
          showIcon
          style={{ maxWidth: 500 }}
        />
      </div>
    )
  }

  const combinedActionButtons = useMemo(() => {
    const pageSizeControl = (
      <Horizontal
        key='page-size-control'
        gap={8}
        alignItems='center'
        style={{ whiteSpace: 'nowrap' }}
      >
        <Texto>Rows</Texto>
        <Select
          size='small'
          value={pageSize}
          onChange={(value) => onPageSizeChange(Number(value))}
          options={pageSizeOptions.map((value) => ({
            value,
            label: value.toLocaleString(),
          }))}
          style={{ width: 120 }}
        />
      </Horizontal>
    )

    return [...actionButtons, pageSizeControl]
  }, [actionButtons, onPageSizeChange, pageSize, pageSizeOptions])

  return (
    <div className='h-full'>
      <GraviGrid
        loading={loading}
        agPropOverrides={gridOptions}
        updateEP={onUpdate}
        externalRef={gridApiRef}
        controlBarProps={{
          hidden: false,
          title: `${entityType.charAt(0).toUpperCase() + entityType.slice(1)}`,
          actionButtons: combinedActionButtons,
          customSearchBar,
          rowCount: totalRows,
        }}
        enableSearch={true}
        height='calc(100vh - 200px)'
        emptyStateProps={{
          description: loading ? 'Loading...' : `No ${entityType} found`,
        }}
      />
    </div>
  )
}
